import React, { useMemo, useState } from "react";
import { Badge } from "@shared/ui/badge";
import { EyeIcon } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { useNavigate } from "react-router-dom";
import { useGetCompletedMeterRun, useMeterRunExport } from "@features/mx/meterReads/hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import CycleListFilters from "../../filters/CycleListFilters";

interface MeterRun {
  id: number;
  meterSchedule: {
    id: number;
    meterCycle: {
      id: number;
      meterCycleName: string;
      meterType: string;
      productCode: string[];
    };
  };
  summary: {
    meterCount: number;
    consumerCount: number;
    readingCount: number;
    rcntCount: number;
    faultyCount: number;
    normalCount: number;
  };
  status: string;
  month: string;
  lastRunDate: string;
  nextRunDate: string;
  completionPercentage: number;
  dispatchStatus: string;
}

const formatMonthToYearMonth = (month: string): string => {
  if (!month) return "";
  const currentYear = new Date().getFullYear();
  const map: Record<string, string> = {
    january: "01",
    february: "02",
    march: "03",
    april: "04",
    may: "05",
    june: "06",
    july: "07",
    august: "08",
    september: "09",
    october: "10",
    november: "11",
    december: "12",
  };
  return map[month.toLowerCase()]
    ? `${currentYear}-${map[month.toLowerCase()]}`
    : "";
};

const CompletedCycleTable: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = PAGE_SIZE;
  const remoteUtilityId = getRemoteUtilityId();
  const meterType = "PHOTO_METER";

  // Build API params based on filters
  const apiParams = useMemo(() => {
    const params: any = {
      remote_utility_id: remoteUtilityId,
      meter_type: meterType,
      page: currentPage,
      page_size: pageSize,
      isCompleted: true, // indicates history fetch
    };

    if (searchQuery.trim()) params.search = searchQuery.trim();
    if (filters.month && filters.month !== "all")
      params.month_year = formatMonthToYearMonth(filters.month);
    if (filters.status && filters.status !== "all")
      params.run_status = filters.status;
    if (filters.sortBy && filters.sortBy !== "all")
      params.ordering = filters.sortBy;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;

    return params;
  }, [remoteUtilityId, meterType, currentPage, pageSize, searchQuery, filters]);

  const { data: meterRunData, isLoading } = useGetCompletedMeterRun(apiParams);

  const { exportCSV, isExporting } = useMeterRunExport({
    remote_utility_id: remoteUtilityId,
    meter_type: meterType,
    run_status:
      filters.status && filters.status !== "all" ? filters.status : undefined,
    month_year:
      filters.month && filters.month !== "all" ? filters.month : undefined,
  });

  // --- handlers ---
  const handleFilterSubmit = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSearch = (term: string) => setSearchQuery(term);
  const handlePageChange = (p: number) => setCurrentPage(p);
  const handleExport = () => exportCSV();
  const handleViewCycle = (id: string) =>
    navigate(`/mx/meterread/photo-meter/${id}`);

  // --- columns ---
  const columns: TableColumn<MeterRun>[] = [
    {
      key: "meterCycleName",
      header: "Read Cycle",
      type: "custom",
      render: (_, row) => row.meterSchedule.meterCycle.meterCycleName,
    },
    { key: "month", header: "Month", type: "text" },
    {
      key: "meters",
      header: "Meters",
      type: "text",
      render: (_, row) => row?.summary?.meterCount,
    },
    {
      key: "consumers",
      header: "Consumers",
      type: "text",
      render: (_, row) => row?.summary?.consumerCount,
    },
    {
      key: "reading",
      header: "Readings",
      type: "text",
      render: (_, row) => row?.summary?.readingCount,
    },
    {
      key: "summary",
      header: "Meter Quality",
      type: "custom",
      render: (_, row) => (
        <div className="flex flex-wrap gap-1">
          <Badge className="bg-green-100 text-green-800 text-xs">
            Normal: {row.summary?.normalCount}
          </Badge>
          <Badge className="bg-red-100 text-red-800 text-xs">
            Faulty: {row.summary?.faultyCount}
          </Badge>
          <Badge className="bg-amber-100 text-amber-800 text-xs">
            RCNT: {row.summary?.rcntCount}
          </Badge>
        </div>
      ),
    },
    {
      key: "completionPercentage",
      header: "Completion %",
      type: "custom",
      render: (value) => (
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500"
              style={{ width: `${value}%` }}
            />
          </div>
          <span className="text-sm font-medium">{value.toFixed(1)}%</span>
        </div>
      ),
    },
    {
      key: "dispatchStatus",
      header: "Status",
      type: "badge",
    },
    {
      key: "nextRunDate",
      header: "Date Range",
      type: "text",
      render: (_, row) => (
        <span>
          {row?.lastRunDate} - {row.nextRunDate}
        </span>
      ),
    },
  ];

  const actions: TableAction<MeterRun>[] = [
    {
      label: "View Cycle",
      icon: <EyeIcon size={20} className="text-gray-500" />,
      onClick: (row) => handleViewCycle(row.id.toString()),
    },
  ];

  const totalItems = meterRunData?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const displayedData = meterRunData?.results || [];

  return (
    <GenericTable
      data={displayedData}
      columns={columns}
      rowKey="id"
      pagination={{
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage: pageSize,
        startItem: (currentPage - 1) * pageSize + 1,
        endItem: Math.min(currentPage * pageSize, totalItems),
      }}
      onPageChange={handlePageChange}
      onExport={handleExport}
      exportLoading={isExporting}
      onSearch={handleSearch}
      searchPlaceholder="Search by cycle name..."
      actions={actions}
      actionsType="icons"
      filterPanel={<CycleListFilters onSubmit={handleFilterSubmit} />}
      emptyMessage="No completed photo read cycles found matching your criteria"
      loading={isLoading}
    />
  );
};

export default CompletedCycleTable;
